import { jeIos } from './pwa-instalacija.service';

/**
 * Koje rute su „aplikacija” (PWA) i koji manifest im pripada.
 *
 * - Izvođač: `/dashboard/**` → `/manifest.webmanifest` (scope `/dashboard/`).
 * - Javne stranice (naslovnica, forma klijenta, prijava …) nemaju manifest, pa preglednik
 *   na njima ne nudi instalaciju, a service worker se s njih ne registrira.
 * - Klijent (K3): `/k/**` (scope `/k/`, id `/k/`); `/kontakt` ili `/kx` nisu `/k`.
 *   - Osobni link `/k/<token>/**` na **iPhoneu i iPadu** → `/k/<token>/manifest.webmanifest`: isti manifest
 *     klijenta, ali sa `start_url` `/k/<token>`. Ikona na početnom zaslonu tako otvara upit i kad instalirana
 *     aplikacija nema localStorage preglednika (iOS: aplikacija s početnog zaslona ima spremište odvojeno od
 *     Safarija). Manifest za token poslužuje nginx (`nginx/default.conf`), bez backenda.
 *   - **Drugdje** (Android, računalo) i za `/k`, `/k/` i token neispravnog oblika →
 *     `/manifest-klijent.webmanifest` (`start_url` `/k/`: zadnji link iz localStorage ili uputa). Chrome na Androidu
 *     pri instalaciji šalje manifest Googleovu poslužitelju (WebAPK), pa manifest ne smije nositi osobni link;
 *     instalirana aplikacija na Androidu dijeli localStorage s Chromeom, pa `/k/` i dalje otvara zadnji upit.
 */
export interface PwaManifest {
  /** Putanja bez kose crte na kraju; pokriva `prefiks` i sve ispod `prefiks/`. */
  readonly prefiks: string;
  readonly href: string;
  /** Manifest koji ovisi o putanji i uređaju (npr. po tokenu klijenta na iOS-u); `null` znači `href`. */
  readonly hrefZaPutanju?: (putanja: string, uredjaj?: Uredjaj) => string | null;
}

/** Ono što o uređaju treba za izbor manifesta (`navigator`). */
export interface Uredjaj {
  readonly userAgent: string;
  readonly maxTouchPoints: number;
}

/** Trenutni preglednik (nema ga izvan preglednika). */
export function trenutniUredjaj(): Uredjaj | undefined {
  return typeof navigator === 'undefined' ? undefined : navigator;
}

/**
 * Smije li manifest nositi osobni link: samo iPhone, iPod i iPad (iPadOS se predstavlja kao Mac, ali ima više
 * dodirnih točaka). Na iOS-u instalaciju radi sam uređaj, bez slanja manifesta trećoj strani.
 */
export function manifestPoTokenuNaUredaju(uredjaj: Uredjaj | undefined = trenutniUredjaj()): boolean {
  return !!uredjaj && jeIos(uredjaj.userAgent ?? '', uredjaj.maxTouchPoints ?? 0);
}

/**
 * Oblik tokena osobnog linka klijenta: base64url, backend daje 43 znaka. Isti oblik provjerava
 * nginx za `/k/<token>/manifest.webmanifest` (ostalo je 404) i `klijent-token.ts`.
 */
export const OBLIK_TOKENA_KLIJENTA = /^[A-Za-z0-9_-]{8,128}$/;

/**
 * `/k/<token>/…` na iPhoneu ili iPadu → manifest tog tokena; ostalo (`/k`, `/k/`, neispravan token, Android,
 * računalo) → `null` (generički manifest klijenta).
 */
export function manifestKlijentaPoTokenu(putanja: string, uredjaj: Uredjaj | undefined = trenutniUredjaj()): string | null {
  if (!manifestPoTokenuNaUredaju(uredjaj)) {
    return null;
  }
  const [, k, token] = putanja.split('/');
  return k === 'k' && token !== undefined && OBLIK_TOKENA_KLIJENTA.test(token)
    ? `/k/${token}/manifest.webmanifest`
    : null;
}

export const PWA_MANIFESTI: readonly PwaManifest[] = [
  { prefiks: '/dashboard', href: '/manifest.webmanifest' },
  { prefiks: '/k', href: '/manifest-klijent.webmanifest', hrefZaPutanju: manifestKlijentaPoTokenu },
];

/** Putanja iz URL-a routera (bez upita i sidra). */
export function putanjaIzUrla(url: string): string {
  const kraj = url.search(/[?#]/);
  return kraj === -1 ? url : url.slice(0, kraj);
}

/** Manifest za URL (na tom uređaju) ili `null` (javna stranica). `/dashboardx` nije `/dashboard`. */
export function manifestZaUrl(url: string, manifesti: readonly PwaManifest[] = PWA_MANIFESTI,
                              uredjaj: Uredjaj | undefined = trenutniUredjaj()): string | null {
  const putanja = putanjaIzUrla(url);
  const pogodak = manifesti.find(m => putanja === m.prefiks || putanja.startsWith(`${m.prefiks}/`));
  if (!pogodak) {
    return null;
  }
  return pogodak.hrefZaPutanju?.(putanja, uredjaj) ?? pogodak.href;
}

export function jePwaRuta(url: string, manifesti: readonly PwaManifest[] = PWA_MANIFESTI): boolean {
  return manifestZaUrl(url, manifesti) !== null;
}
